import { z } from 'zod';

/**
 * Shared client-side env schema. Only VITE_* values are validated here —
 * they are the only values guaranteed to exist in the browser bundle.
 *
 * SECURITY:
 *   - Never add a secret (SERVICE_ROLE, *_SECRET, *_PRIVATE_KEY, JWT_SECRET)
 *     to this schema. Any VITE_* var is shipped to the browser.
 *   - URL must be https unless it points at localhost / loopback / private LAN
 *     addresses for local dev and same-Wi-Fi mobile testing.
 */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]']);

function isPrivateLanHost(hostname: string): boolean {
  const parts = hostname.split('.').map((part) => Number(part));
  if (
    parts.length !== 4 ||
    parts.some((part) => !Number.isInteger(part) || part < 0 || part > 255)
  ) {
    return false;
  }

  const a = parts[0]!;
  const b = parts[1]!;
  return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
}

const httpsOrLocal = z
  .string()
  .min(1, { message: 'URL env var is required' })
  .refine(
    (raw) => {
      try {
        const u = new URL(raw);
        if (u.protocol === 'https:') return true;
        if (u.protocol === 'http:' && LOCAL_HOSTS.has(u.hostname)) return true;
        if (u.protocol === 'http:' && isPrivateLanHost(u.hostname)) return true;
        return false;
      } catch {
        return false;
      }
    },
    {
      message: 'Must be a valid https:// URL (http:// only allowed for local dev hosts)',
    },
  );

const envSchema = z.object({
  VITE_SUPABASE_URL: httpsOrLocal.describe('Supabase project URL'),
  VITE_SUPABASE_ANON_KEY: z
    .string()
    .min(40, { message: 'VITE_SUPABASE_ANON_KEY looks too short to be a real JWT' }),
  // Public root origin of the storefront app. buildStoreUrl(slug) derives
  // canonical tenant-host URLs from this root, while preview/canvas/cart/auth
  // helpers intentionally stay on this root origin.
  VITE_PUBLIC_STOREFRONT_URL: httpsOrLocal.describe(
    'Public root origin of the storefront app (e.g. http://localhost:5174 in dev, https://dukanti.co in prod). Used to derive tenant storefront URLs.',
  ),
  VITE_PUBLIC_ADMIN_URL: z
    .union([
      httpsOrLocal.describe(
        'Optional public base URL of the admin app. Used by the Storefront Builder live-canvas bridge for exact origin checks.',
      ),
      z.literal(''),
      z.undefined(),
    ])
    .transform((value) => value ?? ''),
  VITE_PAYPAL_CLIENT_ID: z.string().trim().optional().default(''),
});

export type ClientEnv = z.infer<typeof envSchema>;

function readProcessEnv(key: keyof ClientEnv): string | undefined {
  const maybeProcess = (globalThis as { process?: { env?: Record<string, string | undefined> } })
    .process;
  return maybeProcess?.env?.[key];
}

function parseEnv(): ClientEnv {
  // Direct access: Vite / Vitest exposes `import.meta.env.VITE_*` so that
  // `vi.stubEnv` reaches the same values browser code will see. Playwright
  // worker imports run as plain Node ESM, where `import.meta.env` is absent,
  // so each public VITE key falls back to `process.env` for test-support code.
  //
  // F-004 L3 warning — MAINTAINER NOTE:
  //   When adding a new VITE_* env var, you MUST update BOTH:
  //     (1) the Zod `envSchema` above, AND
  //     (2) the `raw` object below.
  //   Forgetting (2) makes Zod see `undefined` for the key and throw a
  //   required-field error that points at the schema rather than this
  //   block — maddening to debug. Keep the two sides in lockstep, and
  //   make sure env.test.ts exercises the new key's parse path
  //   (round-trip) so the drift is caught at test time.
  const raw = {
    VITE_SUPABASE_URL: import.meta.env?.VITE_SUPABASE_URL ?? readProcessEnv('VITE_SUPABASE_URL'),
    VITE_SUPABASE_ANON_KEY:
      import.meta.env?.VITE_SUPABASE_ANON_KEY ?? readProcessEnv('VITE_SUPABASE_ANON_KEY'),
    VITE_PUBLIC_STOREFRONT_URL:
      import.meta.env?.VITE_PUBLIC_STOREFRONT_URL ?? readProcessEnv('VITE_PUBLIC_STOREFRONT_URL'),
    VITE_PUBLIC_ADMIN_URL:
      import.meta.env?.VITE_PUBLIC_ADMIN_URL ?? readProcessEnv('VITE_PUBLIC_ADMIN_URL'),
    VITE_PAYPAL_CLIENT_ID:
      import.meta.env?.VITE_PAYPAL_CLIENT_ID ?? readProcessEnv('VITE_PAYPAL_CLIENT_ID'),
  };
  const result = envSchema.safeParse(raw);
  if (!result.success) {
    const issues = result.error.issues
      .map((i) => `  - ${i.path.join('.')}: ${i.message}`)
      .join('\n');
    throw new Error(
      `[@dukanti/shared/env] Invalid client environment:\n${issues}\n\n` +
        `Copy apps/<app>/.env.example to .env.local and fill in real values.`,
    );
  }
  return result.data;
}

export const env: ClientEnv = parseEnv();
